<script setup lang="ts">
import { ref, computed } from 'vue'

import {useStore} from 'vuex'
import {useRoute, useRouter} from 'vue-router'

import { MoreHorizontal } from 'lucide-vue-next'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { DialogEditMember } from '@/components/dialogs'
import DialogConfirm from '@/components/dialogs/DialogConfirm.vue'
import { useCheckProjectAccess } from '@/composables/useCheckAccess'

import type { Member } from '@/types'

const store = useStore()
const router = useRouter()
const route = useRoute()
const projectId = computed(() => route.params.project_id)
const project = computed(() => store.getters['project/current'])
const currentMember = computed(() => store.getters['user/getMember'])

const props = defineProps<{
  member: Member
}>()
const isCurrentUser = computed(() => currentMember.value.member_id === props.member.member_id)

const canEdit = computed(() => useCheckProjectAccess(project.value, 'project_member', ['update']))
const canDelete = computed(() => useCheckProjectAccess(project.value, 'project_member', ['delete']))

const dialogEditMember = ref(null as any)
const confirmDialog = ref<InstanceType<typeof DialogConfirm> | null>(null)

const edit = (ev: any) => {
  dialogEditMember.value.open(props.member, true)
}

const remove = () => {
  confirmDialog.value?.open()
}

const confirmRemove = async () => {
  const deleted = await store.dispatch('project/deleteMember', {
    projectId: projectId.value,
    memberId: props.member.project_member_id
  })

  if (deleted && isCurrentUser.value) {
    store.dispatch('project/unlistProject', project.value)
    router.push({ name: 'Dashboard' })
  }
}
</script>

<template>
  <template v-if="canEdit || canDelete || isCurrentUser">
    <DialogEditMember ref="dialogEditMember" :member="props.member" editMode />
    <DialogConfirm
      ref="confirmDialog"
      :title="isCurrentUser ? 'Leave project' : 'Delete member'"
      :description="isCurrentUser
        ? 'Are you sure you want to leave this project? You will lose access to it.'
        : `Are you sure you want to remove ${props.member.display_name || 'this member'} from the project?`"
      :confirmText="isCurrentUser ? 'Leave' : 'Delete'"
      variant="error"
      @confirm="confirmRemove"
    />
    <DropdownMenu>
      <DropdownMenuTrigger as-child>
        <Button variant="ghost" class="w-8 h-8 p-0">
          <span class="sr-only">Open menu</span>
          <MoreHorizontal class="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuItem v-if="canEdit" @click="edit">Edit</DropdownMenuItem>

        <DropdownMenuItem
            v-if="canDelete || isCurrentUser"
            @click="remove" class="text-red-700">{{ isCurrentUser ? 'Leave' : 'Delete' }}</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  </template>
</template>
